import React, { useEffect } from 'react';
import { DEBOUNCE_DURATION, RoutePath, SPACING } from '../../generic/config';
import Searcher from '../../generic/Searcher';
import { Context } from '../../App';
import { debounce } from 'lodash';
import { QueryKeys } from '../../api/base/react-query';
import { searchForCustomers } from '../../api/customer-controller';
import { useQuery } from 'react-query';
import Table, { TableProps } from '../../base-components/Table';
import { useNavigate } from 'react-router-dom';
import EditorParent from '@/base-components/EditorParent';
import Card from '@/base-components/Card';
import { CustomerHit } from '@/model/customer-hit-schema';
import { Button } from '@/components/ui/button';
import { MultiLine } from '@/base-components/MultiLine';
import { Translations } from '@/generic/Translations';
import { Pager } from '../order/OrderSearchPage';
import { Paged } from '../../model/paged';

const CustomerSearchPage: React.FC = () => {
    const { translations } = React.useContext(Context);
    const navigate = useNavigate();

    return (
        <EditorParent title={translations.model.customer.search.title} description={translations.model.customer.search.description}>
            <CustomerSearchWithResults href={(row: CustomerHit) => `${RoutePath.Customers}/${row.id}`}>
                <Button my={SPACING} onClick={() => navigate(`${RoutePath.Customers}/new`)}>{translations.common.registerNew}</Button>
            </CustomerSearchWithResults>
        </EditorParent>
    );
};

export default CustomerSearchPage;

export const CustomerNameCell = ({ row }: { row: CustomerHit | null }) => {
    if (!row) {
        return "-";
    };

    return <MultiLine line1={`${row.firstName} ${row.lastName ?? ""} ${row.externalId ? `(${row.externalId})` : ""}`} line2={row.identificationNumber} />;
}

export const getTableProps = (translations: Translations, rows: CustomerHit[], onRowClick?: (row: CustomerHit) => void, href?: (row: CustomerHit) => string, isLoading?: boolean): TableProps<CustomerHit> => {
    return {
        columns: [
            { title: translations.model.customer.id, scale: 1, getter: (r) => r.id },
            { title: translations.model.customer.name, scale: 2, getter: (r) => CustomerNameCell({ row: r }) },
            { title: translations.model.customer.identificationNumber.id, scale: 2, getter: (r) => r.identificationNumber },
        ],
        rows,
        isLoading,
        onRowClick,
        href,
    };
}

export const CustomerSearchWithResults = ({ children, onRowClick, href }: { children?: React.ReactNode; onRowClick?: (row: CustomerHit) => void; href?: (row: CustomerHit) => string }) => {
    const { translations } = React.useContext(Context);
    const [page, setPage] = React.useState(0);

    const doSearch = async () => {
        return await searchForCustomers(filter, page);
    };

    const customerHits = useQuery<Paged<CustomerHit>>([QueryKeys.customers, page], doSearch);

    const [filter, setFilter] = React.useState("");

    useEffect(() => {
        setPage(0);
        customerHits.refetch();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = debounce((value: string) => {
        setFilter(value);
    }, DEBOUNCE_DURATION);

    const onChange = (value: string) => {
        handleChange(value);
    };

    const tableProps = getTableProps(translations, customerHits?.data?.items ?? [], onRowClick, href, customerHits.isLoading);

    return (
        <>
            <Searcher {...translations.model.customer.search} onChange={onChange}>
                {children}
            </Searcher>
            <Table tableProps={tableProps} />
            <Pager page={page} setPage={setPage}
                total={customerHits.data?.total ?? 0} size={customerHits.data?.size ?? 0}
                count={customerHits.data?.items.length ?? 0} />
        </>
    );
}